import { useEffect, useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router-dom'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { useBrowseHistory } from '../../context/browse-history-context'

const NON_BROWSE_PATHS = [/^\/profile\/?$/, /^\/user\/[^/]+\/?$/]

const MAX_ITEMS = 12

const DrawerRoot = styled(Box)(({ theme }) => ({
  position: 'fixed',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
}))

const CollapsedTab = styled('button')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(1.25, 0.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRight: 'none',
  borderRadius: '10px 0 0 10px',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
  '&:hover': {
    color: theme.palette.text.primary,
    borderColor: '#8B1E3F',
  },
}))

const TabLabel = styled(Typography)({
  writingMode: 'vertical-rl',
  fontSize: 11,
  letterSpacing: 1,
  textTransform: 'uppercase',
})

const Panel = styled(Box)(({ theme }) => ({
  width: 150,
  maxHeight: '62vh',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.divider}`,
  borderRight: 'none',
  borderRadius: '12px 0 0 12px',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
  overflow: 'hidden',
}))

const PanelHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.75, 0.75, 0.75, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const PanelList = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

const ItemWrapper = styled(Box)({
  position: 'relative',
  '&:hover .recent-remove': { opacity: 1 },
})

const ItemPoster = styled('img')(({ theme }) => ({
  width: '100%',
  aspectRatio: '2 / 3',
  objectFit: 'cover',
  display: 'block',
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  backgroundColor: theme.palette.background.default,
  transition: 'border-color 0.2s ease',
  '&:hover': { borderColor: '#8B1E3F' },
}))

const ItemPosterFallback = styled(Box)(({ theme }) => ({
  width: '100%',
  aspectRatio: '2 / 3',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  fontSize: 24,
  backgroundColor: theme.palette.background.default,
}))

const RemoveButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  lineHeight: 1,
  color: theme.palette.common.white,
  backgroundColor: 'rgba(0,0,0,0.7)',
  opacity: 0,
  transition: 'opacity 0.15s ease',
  '&:hover': { backgroundColor: '#8B1E3F' },
}))

const RecentDrawer = () => {
  const { recentViews, removeView } = useBrowseHistory()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  // Always start collapsed: any page change folds the drawer back to its tab.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  const onBrowsePage = NON_BROWSE_PATHS.some((pattern) =>
    pattern.test(pathname),
  )
  if (onBrowsePage || recentViews.length === 0) return null

  const items = recentViews.slice(0, MAX_ITEMS)

  return (
    <DrawerRoot>
      {open ? (
        <Panel>
          <PanelHeader>
            <Typography fontSize={12} fontWeight={600} letterSpacing={0.5}>
              RECENT
            </Typography>
            <Tooltip title='Collapse'>
              <IconButton
                size='small'
                onClick={() => setOpen(false)}
                aria-label='Collapse recent drawer'
              >
                <ChevronRightRoundedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </PanelHeader>
          <PanelList>
            {items.map((item) => {
              const type =
                item.mediaType.toLowerCase() === 'movie' ? 'movie' : 'show'
              return (
                <ItemWrapper key={`${item.mediaType}-${item.tmdbId}`}>
                  {item.posterPath ? (
                    <ItemPoster
                      src={item.posterPath}
                      alt={item.title}
                      title={item.title}
                      onClick={() =>
                        navigate(`/details/${type}/${item.tmdbId}`)
                      }
                    />
                  ) : (
                    <ItemPosterFallback
                      title={item.title}
                      onClick={() =>
                        navigate(`/details/${type}/${item.tmdbId}`)
                      }
                    >
                      {item.title.slice(0, 1).toUpperCase()}
                    </ItemPosterFallback>
                  )}
                  <RemoveButton
                    className='recent-remove'
                    onClick={() => removeView(item.mediaType, item.tmdbId)}
                    aria-label={`Remove ${item.title} from recently viewed`}
                  >
                    ✕
                  </RemoveButton>
                </ItemWrapper>
              )
            })}
          </PanelList>
        </Panel>
      ) : (
        <CollapsedTab
          onClick={() => setOpen(true)}
          aria-label='Open recently viewed'
        >
          <HistoryRoundedIcon fontSize='small' />
          <TabLabel>Recent</TabLabel>
        </CollapsedTab>
      )}
    </DrawerRoot>
  )
}

export default RecentDrawer
